import React, { useState, useEffect } from 'react';
import { Box, Snackbar, Alert, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { get, post } from '../../services/https';
import withComponentAuth from '../../hoc/withComponentAuth';
import Pagination from '../../components/atoms/Pagination';
import PurchaseTable from './components/molecules/PurchaseTable';
import CreateLeadDialog from './components/molecules/CreateLeadDialog';
import { SmartFilter } from '../../components/common/SmartFilter';
import PurchaseStatusFilter from '../../components/ui/filters/purchaseStatus';
import { debounce } from '../../utils/helper';
import { usePaginationParams } from '../../hooks/usePaginationParams';
import { useScrollRestoration } from '../../hooks/useScrollRestoration';

interface User {
  _id: string;
  userName: string;
  phoneNumber: string;
  countryCode: string;
  email?: string;
  type: string;
}

interface Product {
  _id: string;
  name: string;
  brand: string;
  model: string;
  acType: string;
  tonnage: number;
  customerPrice: number;
}

interface PurchaseLead {
  _id: string;
  quantity: number;
  unitPrice: number;
  status: 'NEW' | 'CONTACTED' | 'FOLLOW_UP' | 'CONVERTED' | 'LOST';
  source: string;
  createdAt: string;
  soldAmount?: number;
  user: {
    name: string;
    phoneNumber: string;
  };
  product: {
    name: string;
    brand: string;
    model: string;
    acType?: string;
    tonnage?: number;
    customerPrice?: number;
  };
}

const statusOptions = [
  { value: 'ALL', label: 'All Status' },
  { value: 'NEW', label: 'New' },
  { value: 'CONTACTED', label: 'Contacted' },
  { value: 'FOLLOW_UP', label: 'Follow Up' },
  { value: 'CONVERTED', label: 'Converted' },
  { value: 'LOST', label: 'Lost' },
];

const PurchaseScreen = () => {
  const navigate = useNavigate();

  const { page, rowsPerPage, setPage, setRowsPerPage } = usePaginationParams();
  const [openCreateLead, setOpenCreateLead] = useState(false);

  const [loading, setLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const scrollRef = useScrollRestoration<HTMLDivElement>(!leadsLoading);

  const [users, setUsers] = useState<User[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [purchaseLeads, setPurchaseLeads] = useState<PurchaseLead[]>([]);

  const [leadsSearch, setLeadsSearch] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('ALL');

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  });

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  useEffect(() => {
    fetchPurchaseLeads();
  }, [page, rowsPerPage, leadsSearch, selectedStatus]);

  const fetchProducts = async () => {
    try {
      setProductsLoading(true);
      const response = await get('/api/v1/admin/shop/product-list', {
        params: { page: 1, limit: 50, search: '' },
      });

      const productsData = response.data?.data || [];
      setProducts(
        productsData.map((product: any) => ({
          _id: product._id,
          name: product.name,
          brand: product.brand,
          model: product.model,
          acType: product.acType,
          tonnage: product.tonnage,
          customerPrice: product.customerPrice,
        })),
      );
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
      showSnackbar('Error fetching products', 'error');
    } finally {
      setProductsLoading(false);
    }
  };

  const fetchUsers = async (search: string) => {
    try {
      setLoading(true);
      const response = await post(
        `/api/v1/admin/user/list?page=1&limit=10&search=${search}`,
        {},
      );
      setUsers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
      showSnackbar('Error fetching users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchaseLeads = async () => {
    try {
      setLeadsLoading(true);
      const statusParam =
        selectedStatus === 'ALL' ? undefined : selectedStatus;

      const response = await get('/api/v1/admin/shop/purchase/leads', {
        params: {
          page,
          limit: rowsPerPage,
          search: leadsSearch,
          ...(statusParam ? { status: statusParam } : {}),
        },
      });

      const leadsData = response.data?.data || [];
      setPurchaseLeads(leadsData);

      const paginationData = response.data?.pagination || {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1,
      };
      setPagination(paginationData);
    } catch (error) {
      console.error('Error fetching purchase leads:', error);
      setPurchaseLeads([]);
      showSnackbar('Error fetching purchase leads', 'error');
    } finally {
      setLeadsLoading(false);
    }
  };

  const handleCloseCreateLead = () => {
    setOpenCreateLead(false);
    setProducts([]);
    setProductsLoading(false);
  };

  const showSnackbar = (
    message: string,
    severity: 'success' | 'error' | 'info' | 'warning',
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleAddRequest = () => {
    setOpenCreateLead(true);
    fetchProducts();
  };

  const createNewUser = async (userData: any) => {
    if (!userData.userName || !userData.phoneNumber) {
      showSnackbar('Name and phone number are required', 'error');
      return;
    }

    try {
      setLoading(true);
      const response = await post('/api/v1/admin/user/add-edit', userData);

      if (response.data.status && response.data.data) {
        const newUser = response.data.data;
        setUsers((prev) => [newUser, ...prev]);
        showSnackbar('User created successfully!', 'success');
      } else {
        showSnackbar(response.data.message || 'Failed to create user', 'error');
      }
    } catch (error: any) {
      showSnackbar(
        error.response?.data?.message || 'Error creating user',
        'error',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLead = async (leadData: {
    userId: string;
    productId: string;
    remarks?: string;
  }) => {
    if (!leadData.userId || !leadData.productId) {
      showSnackbar('Please select user and a product', 'error');
      return;
    }

    try {
      setLoading(true);
      const requestBody = {
        userId: leadData.userId,
        productId: leadData.productId,
        remarks: leadData.remarks || undefined,
      };

      const response = await post(
        '/api/v1/admin/shop/purchase/lead',
        requestBody,
      );

      if (response.data.status) {
        showSnackbar('Lead created successfully!', 'success');
        handleCloseCreateLead();
        fetchPurchaseLeads();
      } else {
        showSnackbar(response.data.message || 'Failed to create lead', 'error');
      }
    } catch (error: any) {
      showSnackbar(
        error.response?.data?.message || 'Error creating lead',
        'error',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleViewLead = (id: string) => {
    navigate(`/purchase/lead-details/${id}`);
  };

  const handleSearchRefresh = debounce((search: string) => {
    setLeadsSearch(search);
    setPage(1);
  }, 500);

  const handleStatusFilter = (status: string) => {
    setSelectedStatus(status);
    setPage(1);
  };

  const handleResetFilters = () => {
    setSelectedStatus('ALL');
    setPage(1);
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          maxHeight: '100%',
        }}
      >
        <SmartFilter
          title="Purchase Requests"
          createFilter={['SEARCH', 'FILTER', 'ADD']}
          filterHeader="Filter Purchase Requests"
          addbuttontitle="Add Request"
          onNavigate={handleAddRequest}
          onSearch={handleSearchRefresh}
          onFiltered={handleStatusFilter}
          filterElement={
            <PurchaseStatusFilter
              selectedStatus={selectedStatus}
              statusOptions={statusOptions}
              onFiltered={handleStatusFilter}
              onResetFilter={handleResetFilters}
            />
          }
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            p: 1,
          }}
        >
          <Box
            ref={scrollRef}
            sx={{
              height: '100%',
              maxHeight: '100%',
              overflowY: 'scroll',
              scrollbarWidth: 'none',
            }}
          >
            {purchaseLeads.length <= 0 && !leadsLoading ? (
              <Typography textAlign="center" marginTop="20%" color="#8A8D91">
                No purchase requests found
              </Typography>
            ) : (
              <PurchaseTable
                leads={purchaseLeads}
                loading={leadsLoading}
                onView={handleViewLead}
              />
            )}
          </Box>

          <Box display="block">
            <Pagination
              count={pagination.total}
              page={page - 1}
              rowsPerPage={rowsPerPage}
              onPageChange={(_, p) => setPage(p + 1)}
              onRowsPerPageChange={(e: any) => {
                setRowsPerPage(parseInt(e.target.value, 10));
              }}
            />
          </Box>
        </Box>
      </Box>

      <CreateLeadDialog
        open={openCreateLead}
        onClose={handleCloseCreateLead}
        onSubmit={handleCreateLead}
        users={users}
        products={products}
        loading={loading}
        productsLoading={productsLoading}
        onUserSearch={fetchUsers}
        onCreateUser={createNewUser}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default withComponentAuth(PurchaseScreen);
